import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { v2, type DocumentTemplate } from '@/api/v2Client';
import { ApiErrorNotice } from '@/components/shared/ApiErrorNotice';
import { formatDateTime } from '@/lib/format';

// T-17 D11 — explicit load state (an error is not "an empty list") + the one
// error presenter instead of the raw `ApiError.message` (= the JSON body).
type ListState =
  | { status: 'loading' }
  | { status: 'ready'; templates: DocumentTemplate[] }
  | { status: 'error'; error: unknown };

export function TemplatesListPage() {
  const [state, setState] = useState<ListState>({ status: 'loading' });
  const [moduleFilter, setModuleFilter] = useState<string>('All');
  const [reloadKey, setReloadKey] = useState(0);
  const templates = state.status === 'ready' ? state.templates : null;

  useEffect(() => {
    setState({ status: 'loading' });
    v2.documentTemplates
      .list()
      .then((rows) => setState({ status: 'ready', templates: rows }))
      .catch((err: unknown) => setState({ status: 'error', error: err }));
  }, [reloadKey]);

  // Build the module dropdown from whatever the response actually contains —
  // avoids showing 27 eCTD codes when only 5 templates exist.
  const moduleOptions = useMemo(() => {
    if (!templates) return [];
    const seen = new Set<string>();
    for (const t of templates) {
      if (t.TargetModule) seen.add(t.TargetModule);
    }
    return Array.from(seen).sort();
  }, [templates]);

  const filtered = useMemo(() => {
    if (!templates) return [];
    if (moduleFilter === 'All') return templates;
    return templates.filter((t) => t.TargetModule === moduleFilter);
  }, [templates, moduleFilter]);

  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}
      >
        <h2 style={{ margin: 0 }}>Document Templates</h2>
        <span style={{ color: '#6b7280', fontSize: 14 }}>
          AI-assisted document generation from reusable templates
        </span>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <label htmlFor="module-filter" style={{ fontSize: 14, color: '#374151' }}>
          Module:
        </label>
        <select
          id="module-filter"
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 14,
          }}
        >
          <option value="All">All modules</option>
          {moduleOptions.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      {state.status === 'loading' && <p>Loading…</p>}
      {state.status === 'error' && (
        <ApiErrorNotice
          error={state.error}
          fallback="Failed to load templates"
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      )}
      {templates !== null && filtered.length === 0 && (
        <p style={{ color: '#6b7280' }}>
          {templates.length === 0
            ? 'No templates available. Create one via the API to enable AI-assisted generation.'
            : 'No templates match the current filter.'}
        </p>
      )}
      {filtered.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
              <th style={{ padding: '12px 8px' }}>Name</th>
              <th style={{ padding: '12px 8px' }}>Module</th>
              <th style={{ padding: '12px 8px' }}>Description</th>
              <th style={{ padding: '12px 8px' }}>Scope</th>
              <th style={{ padding: '12px 8px' }}>Updated</th>
              <th style={{ padding: '12px 8px' }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.Id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '12px 8px', fontWeight: 600 }}>
                  <Link to={`/templates/${t.Id}`} style={{ color: '#1B3A6B' }}>
                    {t.Name}
                  </Link>
                </td>
                <td style={{ padding: '12px 8px' }}>{t.TargetModule}</td>
                <td style={{ padding: '12px 8px', color: '#374151' }}>
                  {t.Description ?? '—'}
                </td>
                <td style={{ padding: '12px 8px', fontSize: 13 }}>
                  {t.TenantId === null ? (
                    <span
                      style={{
                        background: '#dbeafe',
                        color: '#1e3a8a',
                        padding: '2px 8px',
                        borderRadius: 4,
                      }}
                    >
                      Global
                    </span>
                  ) : (
                    <span style={{ color: '#6b7280' }}>Tenant</span>
                  )}
                </td>
                <td style={{ padding: '12px 8px' }}>{formatDateTime(t.UpdatedAt)}</td>
                <td style={{ padding: '12px 8px' }}>
                  <Link
                    to={`/templates/${t.Id}`}
                    style={{
                      background: '#1B3A6B',
                      color: '#fff',
                      padding: '6px 12px',
                      borderRadius: 6,
                      textDecoration: 'none',
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    Generate
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
